import { Alert, Autocomplete, Box, Button, Chip, Grid, IconButton, Snackbar, TextField, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import HttpService from "../services/HttpService";
import DeleteIcon from "@mui/icons-material/Delete";
import { useMemo, useState } from "react";

export default function ManageMccCodes(props) {
  
  const [feedback, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });

  const [selectedMcc, setSelectedMcc] = useState([]);
  const [inputValue, setInputValue] = useState("");

  const [mccCode, setMccCode] = useState("");
  const [mccDescription, setMccDescription] = useState("");


  const queryClient = useQueryClient();

  // Fetch available MCC codes
  async function fetchMccCodes(inputValue, employerId) {
    const response = await HttpService.getAxiosClient().get(
      window.API_URL + `/mcc/all?search=${inputValue}&idEmployer=${employerId}`
    );
    return response;
  }

  const { data: mccCodesData, isLoading: isLoadingMccCodes } = useQuery(
    ["mccCodes"],
    () => fetchMccCodes(inputValue, props?.ruleInfo?.row?.employerID)
  );

  const debouncedSetInputValue = useMemo(() => {
    let timerId;
    return (value) => {
      clearTimeout(timerId);
      timerId = setTimeout(() => {
        setInputValue(value);
      }, 400);
    };
  }, []);

  // Fetch the current rule to get its MCC codes
  async function fetchRuleMccCodes() {
    const response = await HttpService.getAxiosClient().get(
      window.API_URL + `/rules/spendrules/${props?.ruleInfo?.id}`
    );
    return response;
  }

  const { data, isLoading } = useQuery(
    ["ruleMccCodes", props?.ruleInfo?.id],
    fetchRuleMccCodes
  );

  // Add MCC codes to rule
  const addMccCodeMutation = useMutation(
    (values) => {
      // Use the current rule data from the query result instead of potentially stale props
      // Add safety check in case data is not yet loaded
      if (!data || !data.data) {
        console.error("Rule data not available for update");
        return Promise.reject(new Error("Rule data not available"));
      }
      
      const updatedRule = {...data.data};
      // Check if values is an array or a single object
      if (Array.isArray(values)) {
        updatedRule.mccList = [...(updatedRule.mccList || []), ...values];
      } else {
        updatedRule.mccList = [...(updatedRule.mccList || []), values];
      }

      return HttpService.getAxiosClient().put(
        window.API_URL + `/rules/spendrules/${props.ruleInfo.id}`,
        updatedRule
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["ruleMccCodes", props?.ruleInfo?.id]);
        queryClient.invalidateQueries(["spendRules"]);
        setMccCode("");
        setMccDescription("");
        setFeedback({
          open: true,
          type: "success",
          message: "MCC code(s) added successfully",
          onClose: () => {
            setFeedback({ open: false });
            props.handleClose();
          },
        });
      },
      onError: (error) => {
        setFeedback({
          open: true,
          type: "error",
          message: "Error adding MCC code(s)",
        });
      },
    }
  );
  
  const isAddingMcc = addMccCodeMutation.isLoading;

  // Delete MCC code from rule
  const deleteMccCodeMutation = useMutation(
    (mccToDelete) => {
      // Use the current rule data from the query result instead of potentially stale props
      // Add safety check in case data is not yet loaded
      if (!data || !data.data) {
        console.error("Rule data not available for update");
        return Promise.reject(new Error("Rule data not available"));
      }
      
      const updatedRule = {...data.data};

      if (!updatedRule.mccList) {
        updatedRule.mccList = [];
      }

      // Add safety check for mccList
      if (!updatedRule.mccList) {
        updatedRule.mccList = [];
        return HttpService.getAxiosClient().put(
          window.API_URL + `/rules/spendrules/${props.ruleInfo.id}`,
          updatedRule
        );
      }
      
      updatedRule.mccList = updatedRule.mccList.filter(
        mcc => mcc.code !== mccToDelete.code
      );

      return HttpService.getAxiosClient().put(
        window.API_URL + `/rules/spendrules/${props.ruleInfo.id}`,
        updatedRule
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["ruleMccCodes", props?.ruleInfo?.id]);
        queryClient.invalidateQueries(["spendRules"]);
        setFeedback({
          open: true,
          type: "success",
          message: "MCC code removed successfully",
          onClose: () => {
            setFeedback({ open: false });
            props.handleClose();
          },
        });
      },
      onError: (error) => {
        setFeedback({
          open: true,
          type: "error",
          message: "Error removing MCC code",
        });
      },
    }
  );
  
  const isDeletingMcc = deleteMccCodeMutation.isLoading;
  
  // Combined loading state to track when any mutation is in progress
  const isMutating = isAddingMcc || isDeletingMcc;

  const handleAddMcc = () => {
    if (selectedMcc && selectedMcc.length > 0) {
      // Filter out MCC codes that are already in the rule
      const existingCodes = data?.data?.mccList?.map(mcc => mcc.code) || [];
      const newMccCodes = selectedMcc.filter(mcc => !existingCodes.includes(mcc.code));
      const duplicateCodes = selectedMcc.filter(mcc => existingCodes.includes(mcc.code));
      
      if (duplicateCodes.length > 0) {
        setFeedback({
          open: true,
          type: "warning",
          message: `${duplicateCodes.length} MCC code(s) already exist in the rule`,
        });
        return;
      }

      if (newMccCodes.length === 0) {
        if (duplicateCodes.length === 0) {
          setFeedback({
            open: true,
            type: "error",
            message: "Please select at least one MCC code",
          });
        }
        return;
      }

      // Add all new MCC codes to the rule in a single call
      addMccCodeMutation.mutate(newMccCodes);
      
      setSelectedMcc([]);
    } else {
      setFeedback({
        open: true,
        type: "error",
        message: "Please select at least one MCC code",
      });
    }
  };

  const handleFeedbackClose = (event, reason) => {

    if (reason === "clickaway") {
      return;
    }
    setFeedback({ ...feedback, open: false });
  };

  const columns = [
    { field: "code", headerName: "MCC Code", width: 150 },
    { field: "description", headerName: "Description", width: 350 },
    {
      field: "action",
      headerName: "Action",
      width: 100,
      renderCell: (params) => (
        <IconButton
          onClick={() => {
            deleteMccCodeMutation.mutate(params.row);
          }}
          disabled={isMutating}
        >
          <DeleteIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      {isLoading ? (
        ""
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6">
              Rule: {props?.ruleInfo?.row?.ruleName}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Autocomplete
                sx={{ width: '90%' }}
                multiple
                options={!isLoadingMccCodes ? (mccCodesData?.data || []) : []}
                getOptionLabel={(option) => 
                  `${option.code} - ${option.description}`
                }
                filterSelectedOptions
                value={selectedMcc}
                onChange={(event, newValue) => {
                  setSelectedMcc(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search MCC Codes"
                    placeholder="Type to search..."
                  />
                )}
                disabled={isMutating}
              />
              <Button 
                variant="contained" 
                onClick={handleAddMcc}
                disabled={!selectedMcc || selectedMcc.length === 0 || isMutating}
              >
                Add
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} sx={{ height: 400 }}>
            <DataGrid
              rows={data?.data?.mccList || []}
              columns={columns}
              getRowId={(row) => row.code}
              autoHeight
              density="compact"
            />
          </Grid>
        </Grid>
      )}
      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={feedback.onClose ? feedback.onClose : handleFeedbackClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={feedback.onClose ? feedback.onClose : handleFeedbackClose}
          severity={feedback.type}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
